import { Sale } from '@common/models/sale.model';
import { Injectable } from '@angular/core';
import { SaleItem, SaleItemGroup } from '@common/interfaces/sale-items.interface';
import { from, Observable, of, zip, forkJoin } from 'rxjs';
import { defaultIfEmpty, map, switchMap, take, tap } from 'rxjs/operators';
import { format, startOfDay, endOfDay } from 'date-fns';
import { AngularFirestore, QueryFn } from '@angular/fire/firestore';
import { User } from '@common/interfaces/user.interface';
import { Stand } from '@common/interfaces/stand.interface';
import { ProductsService } from '../products/products.service';
import { StandService } from '../stand/stand.service';
import { BrandService } from '../brand/brand.service';
import { UserModel } from '@common/models/user/user.model';
import { Brand } from '@common/interfaces/brand.interface';
import { isNil } from "lodash";
import { Consumer } from '@common/models/consumer.model';

interface SaleItemsWithLastDoc {
  sales: SaleItemGroup[];
  lastDocument: firebase.firestore.QueryDocumentSnapshot | null;
}

@Injectable({
  providedIn: 'root'
})
export class SaleService {
  public static readonly VentasCollectionName = 'VENTAS';
  public static readonly ArticuloVentasCollectionName = 'ARTICULOS_VENTA';
  constructor(
    private af: AngularFirestore,
    private productService: ProductsService,
    private standService: StandService,
    private brandService: BrandService,
  ) {}

  public deleteSale(idOrdenVenta: Sale['ID_ORDEN_VENTA']): Promise<void> {
    return this.af.doc<Sale>(`${SaleService.VentasCollectionName}/${idOrdenVenta}`).delete();
  }

  public deleteArticuloSale(idArticuloVenta: string): Promise<void> {
    return this.af.doc<Sale>(`${SaleService.ArticuloVentasCollectionName}/${idArticuloVenta}`).delete();
  }

  public static compareSaleItems(s1: SaleItem | SaleItemGroup, s2: SaleItem | SaleItemGroup): boolean {
    return s1 && s2 ? s1.ID_ORDEN_VENTA === s2.ID_ORDEN_VENTA : s1 === s2;
  }

  public static compareSaleItemGroups(sg1: SaleItemGroup, sg2: SaleItemGroup): boolean {
    return SaleService.compareSaleItems(sg1, sg2);
  }

  public getHistorySalesStand(
    fromDate: Date,
    to: Date,
    idConsumer: number,
    limit: number = 20,
    center?,
    ptoVenta?,
    startAfterDoc?: firebase.firestore.QueryDocumentSnapshot
  ): Observable<SaleItemsWithLastDoc> {
    let lastDoc: firebase.firestore.QueryDocumentSnapshot | null = null;

    return this.getAllSalesInAPeriodOfTimeStand(fromDate, to, limit, startAfterDoc, idConsumer, center, ptoVenta).snapshotChanges().pipe(
      tap(salesSnapshots => {
        const lastSnapshot = salesSnapshots[salesSnapshots.length - 1];
        lastDoc = lastSnapshot ? lastSnapshot.payload.doc : null;
      }),
      switchMap(sales =>
        zip(
          ...sales.map(saleSnapshot =>
            // Get needed data
            forkJoin([
              this.af.doc<User>(saleSnapshot.payload.doc.data().Ref_ID_USUARIO).valueChanges().pipe(take(1)),
              this.af.doc<User>(saleSnapshot.payload.doc.data().ID_USUARIO_DEVOLUCION
                ? `USUARIOS/${saleSnapshot.payload.doc.data().ID_USUARIO_DEVOLUCION}`
                : `USUARIOS/${idConsumer}`
              ).valueChanges().pipe(take(1)),
              this.brandService.getBrandByRef(saleSnapshot.payload.doc.data().Ref_ID_MARCA).pipe(take(1)),
              saleSnapshot.payload.doc.data().Ref_ID_STAND ? this.standService.getStandByRef(saleSnapshot.payload.doc.data().Ref_ID_STAND).pipe(take(1)) : of(null),
              this.productService.getByEAN(saleSnapshot.payload.doc.data().CODIGO_EAN, null, null, null, true, true).pipe(take(1)),
              this.af.doc<Consumer>(`CONSUMIDORAS/${saleSnapshot.payload.doc.data().ID_CONSUMIDORA}`).valueChanges().pipe(take(1)) // 👈 Added
]).pipe(
              map(([user, userReturn, brand, stand, product, consumer]) => ({
                id: saleSnapshot.payload.doc.id, // Here is the document's ID
                doc: saleSnapshot.payload.doc,
                ...saleSnapshot.payload.doc.data(),
                FECHA_ARTICULOS_VENTA: ((saleSnapshot.payload.doc.data().FECHA_ARTICULOS_VENTA as unknown) as firebase.firestore.Timestamp).toDate() ?? null,
                FECHA_DEVOLUCION: ((saleSnapshot.payload.doc.data().FECHA_DEVOLUCION as unknown) as firebase.firestore.Timestamp)?.toDate() ?? null,
                FECHA_GRABACION_AV: ((saleSnapshot.payload.doc.data().FECHA_GRABACION_AV as unknown) as firebase.firestore.Timestamp).toDate() ?? null,
                user,
                userReturn,
                brand,
                stand,
                product,
                consumer
              }) as SaleItem),
              defaultIfEmpty()
            )
          )
        ).pipe(defaultIfEmpty([] as SaleItem[]))
      ),
      map(sales => {
        console.log(sales)
        const saleGroups = new Map<number, any>();
        sales.forEach(sale => {
          console.log(sale.FECHA_ARTICULOS_VENTA.getTime())
          let group = saleGroups.get(sale.ID_ORDEN_VENTA);
          if (!group) {
            group = {
              ID_ORDEN_VENTA: sale.ID_ORDEN_VENTA,
              brand: sale.brand,
              date: sale.FECHA_ARTICULOS_VENTA,
              observation: '',
              saleItems: [],
              stand: sale.stand,
              total: 0,
              user: sale.user,
              userReturn: sale.userReturn,
                consumer: sale.consumer // 👈 Add here to SaleItemGroup
            };
            saleGroups.set(sale.ID_ORDEN_VENTA, group);
          }
          group.saleItems.push(sale);
          group.total += sale.TOTAL_ARTICULOS;
        });
        return Array.from(saleGroups.values());
      }),
      switchMap(saleItemGroups =>
        zip(
          ...saleItemGroups.map(saleItemGroup =>
            this.af
              .doc<Sale>(`VENTAS/${saleItemGroup.ID_ORDEN_VENTA}`)
              .get()
              .pipe(
                map(doc => ({ ...saleItemGroup, ID_TICKET: doc.data()?.ID_TICKET, VENTA_GS: doc.data()?.VENTA_GS }))
              )
          )
        ).pipe(
          defaultIfEmpty([])
        )
      ),
      defaultIfEmpty([] as SaleItemGroup[]),
      map(sales => ({
        sales,
        lastDocument: lastDoc
      })));
    
  }
  /**
   * Metedo que devolvera el historico de las ventas
   * @param idConsumer recibe como parametro el ID de la consumidora para la obtencion de su historial de ventras.
   */
  public getHistorySales(idConsumer: number, fromDate: Date, to: Date, idUser?: number): Observable<SaleItemGroup[]> {
    const collectionSaleItems = this.getAllSalesInAPeriodOfTime(idConsumer, fromDate, to);

    return collectionSaleItems.valueChanges().pipe(
      switchMap(sales =>
        zip(
          ...sales.map(sale =>
            // Get needed data
            zip(
              this.af.doc<User>(sale.Ref_ID_USUARIO).valueChanges(), // User Return
              this.af.doc<User>(sale.ID_USUARIO_DEVOLUCION
                  ? `USUARIOS/${sale.ID_USUARIO_DEVOLUCION}`
                  : `USUARIOS/${idUser}`
                ).valueChanges(),
              this.brandService.getBrandByRef(sale.Ref_ID_MARCA), // Brand
              sale.Ref_ID_STAND ? this.standService.getStandByRef(sale.Ref_ID_STAND) : this.standService.getStandByOrderIdSale(sale.ID_ORDEN_VENTA), // Stand
              this.productService.getByEAN(sale.CODIGO_EAN, null, null, null, true, true), // Product
            ).pipe(
              take(1),
              // Map SaleItem
              map(([user, userReturn, brand, stand, product]) => ({
                ...sale,
                FECHA_ARTICULOS_VENTA: ((sale.FECHA_ARTICULOS_VENTA as unknown) as firebase.firestore.Timestamp).toDate(),
                FECHA_DEVOLUCION: ((sale.FECHA_DEVOLUCION as unknown) as firebase.firestore.Timestamp)?.toDate() ?? null,
                FECHA_GRABACION_AV: ((sale.FECHA_GRABACION_AV as unknown) as firebase.firestore.Timestamp).toDate(),
                user,
                userReturn,
                brand,
                stand,
                product
              }) as SaleItem),
              defaultIfEmpty()
            )
          )
        ).pipe(defaultIfEmpty([] as SaleItem[]))
      ),
      // Map SaleItemGroup
      map(sales => sales.reduce(
        (acc, sale) => {
          const datoUsuarioDiv = JSON.parse(localStorage.getItem('userDiv'));
          const idDiv = datoUsuarioDiv.roleData.ID_DIVISION;

          if (sale.brand.ID_DIVISION !== idDiv) return acc;
          const index = acc.findIndex(a => a.saleItems.find(s => SaleService.compareSaleItems(s, sale)));

          // If group already in array just update items and total
          if (index >= 0) {
            return [
              ...acc.slice(0, index),
              {
                ...acc[index],
                saleItems: [...acc[index].saleItems, sale],
                total: acc[index].total + sale.TOTAL_ARTICULOS
              },
              ...acc.slice(index + 1, acc.length)
            ];
          }

          // Else add new group
          return [
            ...acc,
            {
              ID_ORDEN_VENTA: sale.ID_ORDEN_VENTA,
              brand: sale.brand,
              date: sale.FECHA_ARTICULOS_VENTA,
              observation: '',
              saleItems: [sale],
              stand: sale.stand,
              total: sale.TOTAL_ARTICULOS,
              user: sale.user,
              userReturn: sale.userReturn
            }
          ];
        },
        [] as SaleItemGroup[]
      )),
      switchMap(saleItemGroups =>
        zip(
          ...saleItemGroups.map(saleItemGroup =>
            this.af
              .doc<Sale>(`VENTAS/${saleItemGroup.ID_ORDEN_VENTA}`)
              .get()
              .pipe(
                map(doc => ({ ...saleItemGroup, ID_TICKET: doc.data()?.ID_TICKET, VENTA_GS: doc.data()?.VENTA_GS }))
              )
          )
        ).pipe(
          defaultIfEmpty([])
        )
      ),
      defaultIfEmpty([] as SaleItemGroup[])
    );
  }

  public getHistorySalesDetails(idConsumer: number, idUser?: number): Observable<SaleItemGroup[]> {

    const dateTo = new Date();
    const year = dateTo.getFullYear();
    const dateFrom = new Date(year, 0, 1);

    const collectionSaleItems = this.getAllSalesInAPeriodOfTime(idConsumer, dateFrom, dateTo);

    return collectionSaleItems.valueChanges().pipe(
      switchMap(sales =>
        zip(
          ...sales.map(sale =>
            // Get needed data
            zip(
              this.af.doc<User>(sale.Ref_ID_USUARIO).valueChanges(), // User Return
              this.af.doc<User>(sale.ID_USUARIO_DEVOLUCION
                  ? `USUARIOS/${sale.ID_USUARIO_DEVOLUCION}`
                  : `USUARIOS/${idUser}`
                ).valueChanges(),
              this.brandService.getBrandByRef(sale.Ref_ID_MARCA), // Brand
              sale.Ref_ID_STAND ? this.standService.getStandByRef(sale.Ref_ID_STAND) : this.standService.getStandByOrderIdSale(sale.ID_ORDEN_VENTA), // Stand
              this.productService.getByEAN(sale.CODIGO_EAN, null, null, null, true, true), // Product
            ).pipe(
              take(1),
              // Map SaleItem
              map(([user, userReturn, brand, stand, product]) => ({
                ...sale,
                FECHA_ARTICULOS_VENTA: ((sale.FECHA_ARTICULOS_VENTA as unknown) as firebase.firestore.Timestamp).toDate(),
                FECHA_DEVOLUCION: ((sale.FECHA_DEVOLUCION as unknown) as firebase.firestore.Timestamp)?.toDate() ?? null,
                FECHA_GRABACION_AV: ((sale.FECHA_GRABACION_AV as unknown) as firebase.firestore.Timestamp).toDate(),
                user,
                userReturn,
                brand,
                stand,
                product
              }) as SaleItem),
              defaultIfEmpty()
            )
          )
        ).pipe(defaultIfEmpty([] as SaleItem[]))
      ),
      // Map SaleItemGroup
      map(sales => sales.reduce(
        (acc, sale) => {
          const datoUsuarioDiv = JSON.parse(localStorage.getItem('userDiv'));
          const idDiv = datoUsuarioDiv.roleData.ID_DIVISION;
          if (sale.brand.ID_DIVISION !== idDiv) 
            return acc;
          
          const index = acc.findIndex(a => a.saleItems.find(s => SaleService.compareSaleItems(s, sale)));

          // If group already in array just update items and total
          if (index >= 0) {
            return [
              ...acc.slice(0, index),
              {
                ...acc[index],
                saleItems: [...acc[index].saleItems, sale],
                total: acc[index].total + sale.TOTAL_ARTICULOS
              },
              ...acc.slice(index + 1, acc.length)
            ];
          }

          // Else add new group
          return [
            ...acc,
            {
              ID_ORDEN_VENTA: sale.ID_ORDEN_VENTA,
              brand: sale.brand,
              date: sale.FECHA_ARTICULOS_VENTA,
              observation: '',
              saleItems: [sale],
              stand: sale.stand,
              total: sale.TOTAL_ARTICULOS,
              user: sale.user,
              userReturn: sale.userReturn
            }
          ];
        },
        [] as SaleItemGroup[]
      )),
      map( data => {
        while (data.length > 5) {
          data.shift()
        }
        return data;
      }),
      switchMap(saleItemGroups =>
        zip(
          
          ...saleItemGroups.map(saleItemGroup =>
            this.af
              .doc<Sale>(`VENTAS/${saleItemGroup.ID_ORDEN_VENTA}`)
              .get()
              .pipe(
                map(doc => ({ ...saleItemGroup, ID_TICKET: doc.data()?.ID_TICKET, VENTA_GS: doc.data()?.VENTA_GS }))
              )
          )
        ).pipe(
          defaultIfEmpty([])
        )
      ),
      defaultIfEmpty([] as SaleItemGroup[])
    );
  }

  public getAllSalesInAPeriodOfTime(idConsumer: number, fromDate: Date, to: Date, brandId?: Brand['ID_MARCA']) {
    const queryFnWithOutBrand: QueryFn = (ref) => ref
      .where('ID_CONSUMIDORA', '==', idConsumer)
      .where('FECHA_ARTICULOS_VENTA', '>=', startOfDay(fromDate))
      .where('FECHA_ARTICULOS_VENTA', '<=', endOfDay(to));

    let queryFnWithBrand;
    if (brandId) {
      queryFnWithBrand = (ref) => ref
        .where('ID_CONSUMIDORA', '==', idConsumer)
        .where('FECHA_ARTICULOS_VENTA', '>=', startOfDay(fromDate))
        .where('FECHA_ARTICULOS_VENTA', '<=', endOfDay(to))
        .where('ID_MARCA', '==', brandId);
    }

    return this.af.collection<SaleItem>(
      `ARTICULOS_VENTA`,
      brandId ? queryFnWithBrand : queryFnWithOutBrand
    );
  }

  
  public getAllSalesInAPeriodOfTimeStand(fromDate: Date, to: Date, limit: number, startAfterDoc?: firebase.firestore.QueryDocumentSnapshot, idConsumer?: number, center?: number, ptoVenta?: number) {
    return this.af.collection<SaleItem>(
      `ARTICULOS_VENTA`,
      (ref) => {
        let query = ref
          .where('FECHA_ARTICULOS_VENTA', '>=', startOfDay(fromDate))
          .where('FECHA_ARTICULOS_VENTA', '<=', endOfDay(to))
          .orderBy('FECHA_ARTICULOS_VENTA')
          .limit(limit);
    
        if (startAfterDoc) {
          query = query.startAfter(startAfterDoc);
        }
        if (idConsumer) {
          query = query.where('ID_CONSUMIDORA', '==', idConsumer);
        }

        if (center) {
          query = query.where('ID_CENTRO', '==', center);
        }

        if (ptoVenta) {
          query = query.where('ID_STAND', '==', ptoVenta);
        }
  
        return query;
      }
    );
  }

  /**
   * Metodo para devolver un producto
   * @param codigoEAN codigo EAN del producto a devolver
   * @param sale que sera de tipo SaleItem para crear la devolucion del producto
   */
  public returnProduct(saleItem: SaleItem, idUser?: UserModel, units?: number): Observable<any> {
    const returnDate = new Date();
    const {
      user,
      userReturn,
      product,
      stand,
      brand,
      id,
      doc,
      consumer,
      ...returnedSale
    }: SaleItem = ({
      ...saleItem,
      ID_USUARIO_DEVOLUCION: Number(idUser.id),
      TOTAL_ARTICULOS: ((saleItem.TOTAL_ARTICULOS / (saleItem?.UNIDADES_ORIGINAL
        ? saleItem.UNIDADES_ORIGINAL
        : saleItem.UNIDADES))
          * units) * -1,
      TOTAL_ARTICULOS_SIN_DTO: ((saleItem.TOTAL_ARTICULOS_SIN_DTO / (saleItem?.UNIDADES_ORIGINAL
        ? saleItem.UNIDADES_ORIGINAL
        : saleItem.UNIDADES))
          * units) * -1,
      TOTAL_ARTICULOS_SIN_DTO_TARIFA: ((saleItem.TOTAL_ARTICULOS_SIN_DTO_TARIFA / (saleItem?.UNIDADES_ORIGINAL
        ? saleItem.UNIDADES_ORIGINAL
        : saleItem.UNIDADES))
          * units) * -1,
      TOTAL_ARTICULOS_TARIFA: ((saleItem.TOTAL_ARTICULOS_TARIFA / (saleItem?.UNIDADES_ORIGINAL
        ? saleItem.UNIDADES_ORIGINAL
        : saleItem.UNIDADES))
          * units) * -1,
      UNIDADES: units * -1,
      FECHA_DEVOLUCION: returnDate,
    });
    return from(
      this.af.collection<Partial<SaleItem>>('ARTICULOS_VENTA').doc([
        saleItem.CODIGO_EAN,
        saleItem.ID_ORDEN_VENTA.toString(),
        saleItem.ID_TIPO_VENTA.toString(),
        format(returnDate, 'yyyyMMdd HH:mm:ss')
      ].join('-')).set(returnedSale)
    );
  }

  /**
   * Given an idOrdenVenta and it's total, it updates the sale with the new total.
   *
   * @param {Sale['ID_ORDEN_VENTA']} idOrdenVenta
   * @param {Sale['TOTAL_VENTA']} [totalVenta]
   * @param {Sale['TOTAL_VENTA_SIN_DTO']} [totalVentaSinDto]
   * @param {Sale['TOTAL_VENTA_SIN_DTO_TARIFA']} [totalVentaSinDtoTarifa]
   * @param {Sale['TOTAL_VENTA_TARIFA']} [totalVentaTarifa]
   * @return {*} 
   * @memberof SaleService
   */
  public updateSale(
    idOrdenVenta: Sale['ID_ORDEN_VENTA'],
    totalVenta?: Sale['TOTAL_VENTA'],
    totalVentaSinDto?: Sale['TOTAL_VENTA_SIN_DTO'],
    totalVentaSinDtoTarifa?: Sale['TOTAL_VENTA_SIN_DTO_TARIFA'],
    totalVentaTarifa?: Sale['TOTAL_VENTA_TARIFA'],
  ) {
    // https://gist.github.com/ezequieltejada/bb0130a081d33fc5fc7b67de2b55baed
    const newSalesValue = {
      ...(!isNil(totalVenta) && {TOTAL_VENTA: totalVenta}),
      ...(!isNil(totalVentaSinDto) && {TOTAL_VENTA_SIN_DTO: totalVentaSinDto}),
      ...(!isNil(totalVentaSinDtoTarifa) && {TOTAL_VENTA_SIN_DTO_TARIFA: totalVentaSinDtoTarifa}),
      ...(!isNil(totalVentaTarifa) && {TOTAL_VENTA_TARIFA: totalVentaTarifa})
    };
    return this.af.doc<Sale>(`VENTAS/${idOrdenVenta}`).update(newSalesValue)
  }
}
